/**
 * Transfer-only animation overlays — shutters, FOUPs, stocker crane.
 * Static equipment always stays in `FabAmhsInstances3D`.
 */
import React, { useMemo } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { useDynamicEquipmentIds } from './EquipmentLod3D';
import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';
import EquipmentTransferOverlay3D, {
  resolveTransferOverlayElement,
} from './EquipmentTransferOverlay3D';
import type { LayoutElement } from '../../types';

type Props = {
  descriptors: EquipmentRenderDescriptor[];
  /** Optional layout metadata keyed by descriptor id (stocker bay count, etc.). */
  layoutMeta?: Record<string, Pick<LayoutElement, 'metadata' | 'customProperties'>>;
};

const TransferAnimationLayer3D: React.FC<Props> = React.memo(({ descriptors, layoutMeta: _layoutMeta }) => {
  const running = useAppSelector((s) => s.simulation.running);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  const instances = useAppSelector((s) => s.equipment.instances);
  const templates = useAppSelector((s) => s.equipment.templates);

  const candidateIds = useMemo(() => descriptors.map((d) => d.id), [descriptors]);
  const activeIds = useDynamicEquipmentIds(candidateIds);

  const active = useMemo(
    () => descriptors.filter((d) => activeIds.has(d.id)),
    [activeIds, descriptors]
  );

  if (!running || active.length === 0) return null;

  return (
    <group name="TransferAnimationLayer3D">
      {active.map((descriptor) => {
        // Stocker lifter + port FOUP animation lives inside Stocker3D (driveRobot).
        if (descriptor.kind === 'stocker') {
          return null;
        }
        const element = resolveTransferOverlayElement(
          descriptor,
          layoutElements,
          instances,
          templates
        );
        return (
          <EquipmentTransferOverlay3D
            key={descriptor.id}
            descriptor={descriptor}
            element={element}
          />
        );
      })}
    </group>
  );
});

TransferAnimationLayer3D.displayName = 'TransferAnimationLayer3D';

export default TransferAnimationLayer3D;
